import {Injectable, Injector} from '@angular/core';
import {DisplayService} from "@app/presentation/services/display.service";
import {MAT_BOTTOM_SHEET_DATA, MatBottomSheet, MatBottomSheetRef} from "@angular/material/bottom-sheet";
import {MatDialog, MatDialogConfig, MatDialogRef} from "@angular/material/dialog";
import {take} from "rxjs/operators";
import {ModalComponent} from "@app/presentation/services/modal/modal.component";
import {ComponentType} from "@angular/cdk/overlay";
import {Observable} from "rxjs";
import {
    SIDE_SHEET_DATA,
    SideSheetConfig,
    SideSheetService
} from "@app/presentation/services/side-sheet/side-sheet.service";
import {SideSheetRef} from "@app/presentation/services/side-sheet/side-sheet-ref";

export enum MobileModalType {
    BOTTOM_SHEET,
}

export enum DesktopModalType {
    DIALOG,
    SIDE_SHEET,
}

export interface ModalConfig<Data = any> {
    data?: Data;
    disableClose?: boolean;
    // Custom panel class to apply to the dialog.
    dialogPanelClass?: string;
    // Modal type to be displayed on mobile devices.
    mobileModalType?: MobileModalType;
    // Modal type to be displayed on desktop devices.
    desktopModalType?: DesktopModalType;
}

@Injectable()
export class ModalService {

  private readonly DEFAULT_MOBILE_MODAL_TYPE = MobileModalType.BOTTOM_SHEET;
  private readonly DEFAULT_DESKTOP_MODAL_TYPE = DesktopModalType.DIALOG;

  constructor(
      private displayService: DisplayService,
      private bottomSheet: MatBottomSheet,
      private matDialog: MatDialog,
      private sideSheetService: SideSheetService,
  ) {
  }

  /**
   * If the user is on a mobile device, open a bottom sheet, otherwise open a dialog
   * @param {string} message - string - The error message to display
   * @param [onClose] - This is a callback function that will be called when the user clicks on the 'Ok' or 'Cancel' button.
   */
  display(message: string, onClose?: (modalResponse?: boolean) => void ) {
    this.displayService.isMobileDisplay$
        .pipe(take(1))
        .subscribe({
          next: isMobile => {
          if(isMobile) {
            const bottomSheetRef = this.openBottomSheet(message);
            bottomSheetRef.afterDismissed().pipe(take(1)).subscribe({next: onClose});
          } else {
            const dialogRef = this.openDialog(message)
            dialogRef.afterClosed().pipe(take(1)).subscribe({next: onClose});
          }
        }})
  }

    /**
     * If the user is on a mobile device, open a bottom sheet, otherwise open a dialog
     * @param component The component to display in the dialog
     * @param config The configuration for the dialog
     */
  open<R = any, D = any>(component: ComponentType<any>, config?: ModalConfig<D>): Observable<R> {
      const isMobile = this.displayService.isMobileDisplay;
      if(isMobile) {
        return this.openMobileModal<R>(component, config);
      } else {
        return this.openDesktopModal<R>(component, config);
      }
  }

    /**
     * Open a Bottom Sheet with the given component.
     * @param component - The component to display in the Bottom Sheet
     * @param config - The configuration for the BottomSheet
     */
  openComponentInBottomSheet(component: ComponentType<any>, config: ModalConfig = {}): MatBottomSheetRef {
      return this.bottomSheet.open(component,{
          panelClass: 'ep-bottom-sheet',
          ...config,
      })
  }

    private openBottomSheet(message: string): MatBottomSheetRef<any,any> {
        return this.bottomSheet.open(ModalComponent,{
            panelClass: 'ep-bottom-sheet',
            data: { message },
        })
    }

    /**
     * Open a dialog with the given component
     * @param component - The component to display in the dialog
     * @param config - The configuration for the dialog
     */
  openComponentInDialog(component: ComponentType<any>, config: (MatDialogConfig & ModalConfig) = {}): MatDialogRef<any,any> {
        return this.matDialog.open(component,{
            panelClass: config.dialogPanelClass ?? 'custom-modal-container',
            ...config
        })
    }

    /**
     * Open a Side Sheet with the given component.
     * @param component - The component to display in the Side Sheet
     * @param config - The configuration for the Side Sheet
     */
    openComponentInSideSheet(component: ComponentType<any>, config: ModalConfig = {}): SideSheetRef {
        const sideSheetConfig: SideSheetConfig = { data: config.data };
        return this.sideSheetService.open(component, sideSheetConfig);
    }
    
    /**
     * Get the data from inside of from any type of modal.
     */
    static getModalData<Data = any>(injector: Injector): Data | null {
        try {
            return injector.get(MAT_BOTTOM_SHEET_DATA);
        } catch { }
      
        try {
            return injector.get(SIDE_SHEET_DATA);
        } catch { }
      
        return null;
    }

  private openDialog(message: string): MatDialogRef<any,boolean> {
    return this.matDialog.open(ModalComponent,{
      panelClass: 'custom-modal-container',
      data: { message },
    })
  }

    private openMobileModal<R = any>(component: ComponentType<any>, config?: ModalConfig): Observable<R> {
        const modalType = config?.mobileModalType ?? this.DEFAULT_MOBILE_MODAL_TYPE;
        if (modalType === MobileModalType.BOTTOM_SHEET) {
            const bottomSheetRef = this.openComponentInBottomSheet(component, config);
            return bottomSheetRef.afterDismissed().pipe(take(1));
        } else {
            throw new Error(`The modal type ${modalType} is not supported`);
        }
    }

    private openDesktopModal<R = any>(component: ComponentType<any>, config?: ModalConfig): Observable<R> {
        const modalType = config?.desktopModalType ?? this.DEFAULT_DESKTOP_MODAL_TYPE;
        switch (modalType) {
            case DesktopModalType.DIALOG:
                const dialogRef = this.openComponentInDialog(component, config);
                return  dialogRef.afterClosed().pipe(take(1));
            case DesktopModalType.SIDE_SHEET:
                const sideSheetRef = this.openComponentInSideSheet(component, config);
                return sideSheetRef.afterClosedWithValue();
        }
    }
}
